import { apiClient } from './client';
import type { Ontology, OntologyNode, OntologyEdge, OntologyGenerationJob } from '@shared/types/ontology';

/**
 * Ontology API endpoints
 */
export const ontologyApi = {
  /**
   * Get all ontologies for the current user
   */
  // `signal` is for the import verification read (useResilientMutation), which needs a much
  // shorter deadline than env.apiTimeout.
  async getOntologies(signal?: AbortSignal): Promise<Ontology[]> {
    return apiClient.get('/api/ontologies', { signal });
  },

  /**
   * Get a single ontology by ID
   */
  async getOntology(id: string): Promise<Ontology & { generation_job?: OntologyGenerationJob | null; nodes?: any[]; edges?: any[]; can_write?: boolean; is_shared?: boolean; viewing_version?: any }> {
    return apiClient.get(`/api/ontologies/${id}`);
  },

  /**
   * Create a new ontology
   */
  async createOntology(data: {
    name: string;
    description?: string;
    metadata?: Record<string, any>;
  }): Promise<Ontology> {
    return apiClient.post('/api/ontologies', data);
  },

  /**
   * Update an ontology
   */
  async updateOntology(
    id: string,
    data: {
      name?: string;
      description?: string;
      metadata?: Record<string, any>;
      visibility?: 'private' | 'public';
    }
  ): Promise<Ontology> {
    return apiClient.patch(`/api/ontologies/${id}`, data);
  },

  /** Read a single ontology (incl. its visibility). */
  async getById(id: string): Promise<Ontology & { visibility?: 'private' | 'public' }> {
    return apiClient.get(`/api/ontologies/${id}`);
  },

  /**
   * A1 — mine additional KB documents for NEW concepts/relationships and merge
   * them into this ontology's draft. Returns only the new (deduped) nodes/edges;
   * edges may reference existing node ids. The editor places + saves them.
   */
  async extendFromDocuments(
    id: string,
    documentIds: string[],
    nodeCount = 20
  ): Promise<{
    nodes: Array<{ id: string; label: string; definition: string; properties: any[]; position: { x: number; y: number } }>;
    edges: Array<{ id: string; source: string; target: string; label: string; relationshipType: string }>;
    existingCount: number;
  }> {
    return apiClient.post(`/api/ontologies/${id}/extend-from-documents`, { documentIds, nodeCount });
  },

  /**
   * Delete an ontology
   */
  async deleteOntology(id: string): Promise<void> {
    return apiClient.delete(`/api/ontologies/${id}`);
  },

  /**
   * Get all nodes for an ontology
   */
  async getNodes(ontologyId: string): Promise<OntologyNode[]> {
    return apiClient.get(`/api/ontologies/${ontologyId}/nodes`);
  },

  /**
   * Get a single node
   */
  async getNode(ontologyId: string, nodeId: string): Promise<OntologyNode> {
    return apiClient.get(`/api/ontologies/${ontologyId}/nodes/${nodeId}`);
  },

  /**
   * Create a new node
   */
  async createNode(
    ontologyId: string,
    data: {
      nodeId: string;
      label: string;
      definition?: string;
      properties?: Array<{ key: string; value: any }>;
      positionX?: number;
      positionY?: number;
      metadata?: Record<string, any>;
    }
  ): Promise<OntologyNode> {
    return apiClient.post(`/api/ontologies/${ontologyId}/nodes`, data);
  },

  /**
   * Update a node
   */
  async updateNode(
    ontologyId: string,
    nodeId: string,
    data: {
      label?: string;
      definition?: string;
      properties?: Array<{ key: string; value: any }>;
      positionX?: number;
      positionY?: number;
      metadata?: Record<string, any>;
    }
  ): Promise<OntologyNode> {
    return apiClient.patch(`/api/ontologies/${ontologyId}/nodes/${nodeId}`, data);
  },

  /**
   * Delete a node
   */
  async deleteNode(ontologyId: string, nodeId: string): Promise<void> {
    return apiClient.delete(`/api/ontologies/${ontologyId}/nodes/${nodeId}`);
  },

  /**
   * Get all edges for an ontology
   */
  async getEdges(ontologyId: string): Promise<OntologyEdge[]> {
    return apiClient.get(`/api/ontologies/${ontologyId}/edges`);
  },

  /**
   * Get a single edge
   */
  async getEdge(ontologyId: string, edgeId: string): Promise<OntologyEdge> {
    return apiClient.get(`/api/ontologies/${ontologyId}/edges/${edgeId}`);
  },

  /**
   * Create a new edge
   */
  async createEdge(
    ontologyId: string,
    data: {
      edgeId: string;
      sourceNodeId: string;
      targetNodeId: string;
      label?: string;
      relationshipType?: string;
      properties?: Record<string, any>;
      metadata?: Record<string, any>;
    }
  ): Promise<OntologyEdge> {
    return apiClient.post(`/api/ontologies/${ontologyId}/edges`, data);
  },

  /**
   * Update an edge
   */
  async updateEdge(
    ontologyId: string,
    edgeId: string,
    data: {
      label?: string;
      relationshipType?: string;
      properties?: Record<string, any>;
      metadata?: Record<string, any>;
    }
  ): Promise<OntologyEdge> {
    return apiClient.patch(`/api/ontologies/${ontologyId}/edges/${edgeId}`, data);
  },

  /**
   * Delete an edge
   */
  async deleteEdge(ontologyId: string, edgeId: string): Promise<void> {
    return apiClient.delete(`/api/ontologies/${ontologyId}/edges/${edgeId}`);
  },

  /**
   * Search nodes by text
   */
  async searchNodes(
    ontologyId: string,
    query: string,
    options?: {
      limit?: number;
      threshold?: number;
    }
  ): Promise<OntologyNode[]> {
    return apiClient.get(`/api/ontologies/${ontologyId}/search`, {
      params: {
        q: query,
        ...options,
      },
    });
  },

  /**
   * Find similar nodes using vector search
   */
  async findSimilarNodes(
    ontologyId: string,
    nodeId: string,
    options?: {
      limit?: number;
      threshold?: number;
    }
  ): Promise<Array<OntologyNode & { similarity: number }>> {
    return apiClient.get(`/api/ontologies/${ontologyId}/nodes/${nodeId}/similar`, {
      params: options,
    });
  },

  /**
   * Generate embeddings for a node
   */
  async generateEmbedding(ontologyId: string, nodeId: string): Promise<void> {
    return apiClient.post(`/api/ontologies/${ontologyId}/nodes/${nodeId}/embedding`);
  },

  /**
   * Batch update node positions (for graph layout)
   */
  async updateNodePositions(
    ontologyId: string,
    positions: Array<{
      nodeId: string;
      positionX: number;
      positionY: number;
    }>
  ): Promise<void> {
    return apiClient.patch(`/api/ontologies/${ontologyId}/nodes/positions`, {
      positions,
    });
  },

  /**
   * Save a complete ontology (graph + positions + metadata)
   */
  async saveOntology(data: {
    name: string;
    description?: string;
    /**
     * Optional user-typed save label. NOT a version number (migration 051):
     * numbers are assigned at publish time and read from
     * `latest_published_version`.
     */
    save_label?: string | null;
    viewport?: { x: number; y: number; zoom: number };
    // See note on saveDraft: `properties` shape varies between editor
    // versions; backend persists as JSONB so we accept anything here.
    nodes: Array<{
      node_id: string;
      label: string;
      definition?: string;
      properties?: any[];
      position_x: number;
      position_y: number;
      metadata?: Record<string, any>;
      // Provenance columns (migration 039).
      provenance?: 'ai' | 'user' | 'corrected';
      created_by_name?: string | null;
      updated_by_name?: string | null;
      corrected_at?: string | null;
    }>;
    edges: Array<{
      edge_id: string;
      source_node_id: string;
      target_node_id: string;
      label?: string;
      relationship_type?: string;
      properties?: Record<string, any>;
      metadata?: Record<string, any>;
      // Provenance columns (migration 039).
      provenance?: 'ai' | 'user' | 'corrected';
      created_by_name?: string | null;
      updated_by_name?: string | null;
      corrected_at?: string | null;
    }>;
    metadata?: Record<string, any>;
    /** KB documents this ontology was generated from (A17). Persisted as sources. */
    source_document_ids?: string[];
  }): Promise<{
    id: string;
    name: string;
    save_label: string | null;
    nodeCount: number;
    edgeCount: number;
    createdAt: string;
  }> {
    return apiClient.post('/api/ontologies/save', data);
  },

  /** List the KB documents an ontology was generated from (A17). */
  async getOntologySources(
    id: string
  ): Promise<
    Array<{
      knowledge_object_id: string;
      filename: string;
      display_name: string | null;
      knowledge_base_id: string | null;
      knowledge_base_name: string | null;
    }>
  > {
    return apiClient.get(`/api/ontologies/${id}/sources`);
  },

  /**
   * Import an ontology from a JSON-LD document (the inverse of the JSON-LD
   * export). Creates a NEW, unpublished ontology (its draft has no number).
   * `jsonld` may be the parsed object or the raw JSON string.
   */
  async importOntology(data: {
    name?: string;
    jsonld: unknown;
  }): Promise<{ id: string; name: string; save_label: string | null; nodeCount: number; edgeCount: number }> {
    return apiClient.post('/api/ontologies/import', data);
  },

  /**
   * Save vs Publish refactor — Step C wiring.
   *
   * Persist the editor's current graph into the *existing* ontology's draft
   * version. This is the non-destructive equivalent of the legacy
   * `POST /save` flow: same `id`, same `name`, same `version`, but the
   * draft's nodes/edges are replaced with whatever's on the canvas right now.
   *
   * Use this for every Save click after an ontology has been created.
   * For brand-new ontologies (no `id` yet) keep using `saveOntology()`.
   */
  // NOTE: `properties` on nodes/edges is intentionally typed as `any[]` /
  // `Record<string, any>` here. The editor's in-memory shape uses
  // `{ name, type }` for both nodes and edges, while older code used
  // `{ key, value }`. The backend stores them as JSONB and only relies on
  // a few well-known fields, so we let TypeScript stay out of the way and
  // accept whichever shape the editor produces today.
  async saveDraft(
    ontologyId: string,
    data: {
      viewport?: { x: number; y: number; zoom: number };
      nodes: Array<{
        node_id: string;
        label: string;
        definition?: string;
        properties?: any[];
        position_x: number;
        position_y: number;
        metadata?: Record<string, any>;
        // Provenance columns (migration 039).
        provenance?: 'ai' | 'user' | 'corrected';
        created_by_name?: string | null;
        updated_by_name?: string | null;
        corrected_at?: string | null;
      }>;
      edges: Array<{
        edge_id: string;
        source_node_id: string;
        target_node_id: string;
        label?: string;
        relationship_type?: string;
        properties?: Record<string, any>;
        metadata?: Record<string, any>;
        // Provenance columns (migration 039).
        provenance?: 'ai' | 'user' | 'corrected';
        created_by_name?: string | null;
        updated_by_name?: string | null;
        corrected_at?: string | null;
      }>;
      metadata?: Record<string, any>;
    }
  ): Promise<{
    id: string;
    draftVersionId: string;
    nodeCount: number;
    edgeCount: number;
  }> {
    return apiClient.put(`/api/ontologies/${ontologyId}/draft`, data);
  },

  /**
   * Patch ontology metadata (rename / re-describe).
   *
   * Used by the explicit "Rename" affordance. Does NOT touch nodes/edges and
   * does NOT bump the version number — those are owned by saveDraft / publish.
   */
  async patchOntology(
    ontologyId: string,
    data: { name?: string; description?: string | null }
  ): Promise<{
    id: string;
    name: string;
    description: string | null;
    save_label: string | null;
  }> {
    return apiClient.patch(`/api/ontologies/${ontologyId}`, data);
  },

  /**
   * Does the user already own an ontology with that name? An ontology is
   * identified by its name; the save label identifies nothing (migration 051),
   * and there is no "next version" to suggest — version numbers are assigned at
   * publish time.
   */
  async checkNameExists(name: string): Promise<{
    exists: boolean;
    ontologyId?: string;
  }> {
    return apiClient.get('/api/ontologies/check-name', {
      params: { name },
    });
  },
};

// Made with Bob